<template>
  <div class="medical-page">
    <medical-top-bar :search-value.sync="searchKeyword" @search="onSearch" />

    <main class="page-layout">
      <div class="page-header">
        <button class="back-btn" @click="goBack">
          <span class="back-btn__icon">←</span>
          <span>返回</span>
        </button>
        <span class="breadcrumb-separator">/</span>
        <span class="breadcrumb-dept">{{ disease.department_name || "" }}</span>
        <span class="breadcrumb-separator">/</span>
        <span class="breadcrumb-name">{{ disease.name }}</span>
      </div>

      <article id="overview" class="content-card content-card--overview">
        <div class="disease-header">
          <h1 class="disease-title">{{ disease.name }}</h1>
          <span class="disease-subtitle">（什么是{{ disease.name }}？）</span>
        </div>

        <p class="disease-definition" v-if="disease.definition">
          {{ disease.definition }}
        </p>

        <div class="disease-info-list">
          <div
            class="disease-info-item"
            v-if="disease.related_symptoms.length > 0"
          >
            <span class="disease-info-label">相关症状：</span>
            <span class="disease-info-content">{{
              disease.related_symptoms.join("、")
            }}</span>
          </div>
          <div class="disease-info-item" v-if="disease.sub_diseases.length > 0">
            <span class="disease-info-label">细分疾病：</span>
            <span class="disease-info-content">{{
              disease.sub_diseases.join("、")
            }}</span>
          </div>
        </div>
      </article>
      <section class="page-main">
        <article id="western-medicines" class="content-card">
          <div class="section-head">
            <h2>药品推荐</h2>
            <span v-if="filteredMedicines.length > 0"
              >{{ filteredMedicines.length }} 项</span
            >
          </div>

          <div
            v-if="filteredMedicines.length > 0"
            class="medicine-grid medicine-grid--catalog medicine-grid--western"
          >
            <article
              v-for="(item, index) in filteredMedicines"
              :key="'medicine-' + index"
              class="medicine-card medicine-card--western"
              @click="goToDrugDetail(item.ID)"
              style="cursor: pointer"
            >
              <div class="medicine-card__label-row">
                <span v-if="item.Explanation" class="medicine-card__review"
                  >有点评</span
                >
              </div>
              <div class="medicine-card__top">
                <h3>{{ item.MedicineName }}</h3>
              </div>
              <div class="medicine-card__divider"></div>
              <p>{{ item.Indications || "" }}</p>
            </article>
          </div>
          <div v-else class="empty-medicine">
            <div class="empty-medicine__icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 3h12v2H6V3zm0 4h12v2H6V7zm0 4h12v2H6v-2zm0 4h8v2H6v-2z"
                  fill="#cbd5e1"
                />
                <path
                  d="M19 13h-2v-2h-2v2h-2v2h2v2h2v-2h2v-2z"
                  fill="#94a3b8"
                />
              </svg>
            </div>
            <h3 class="empty-medicine__title">暂无推荐药品</h3>
            <p class="empty-medicine__desc">该疾病暂无相关药品推荐信息</p>
          </div>
        </article>
      </section>
    </main>
  </div>
</template>

<script>
import MedicalTopBar from "./components/MedicalTopBar.vue";
import axios from "axios";

export default {
  name: "MedicalDiseaseDetailStyled",
  components: {
    MedicalTopBar,
  },
  data() {
    return {
      loading: false,
      searchKeyword: "",
      apiData: {
        items: [],
        info: {
          description: "",
          symptoms: "",
          subDiseases: "",
        },
      },
    };
  },
  computed: {
    filteredMedicines() {
      if (!this.apiData.items || this.apiData.items.length === 0) {
        return [];
      }
      if (!this.searchKeyword.trim()) {
        return this.apiData.items;
      }
      const keyword = this.searchKeyword.toLowerCase().trim();
      return this.apiData.items.filter(
        (item) =>
          (item.MedicineName &&
            item.MedicineName.toLowerCase().includes(keyword)) ||
          (item.Indications &&
            item.Indications.toLowerCase().includes(keyword)),
      );
    },
    disease() {
      const query = this.$route.query;

      // 从 API 数据中提取药品列表
      const westernMedicines = this.apiData.items
        .filter(
          (item) =>
            item.SourceCategoryID === 1869 ||
            item.SourceCategoryID === 2051 ||
            item.SourceCategoryID === 1904 ||
            item.SourceCategoryID === 1981 ||
            item.SourceCategoryID === 1906,
        )
        .map((item) => ({
          name: item.MedicineName,
          price: item.Manufacturer
            ? `¥${Math.floor(Math.random() * 50 + 10)}`
            : null,
          indication: item.Indications || "",
          has_review: !!item.Explanation,
        }));

      const chineseMedicines = this.apiData.items
        .filter((item) => item.SourceCategoryID === 1899)
        .map((item) => ({
          name: item.MedicineName,
          price: item.Manufacturer
            ? `¥${Math.floor(Math.random() * 50 + 10)}`
            : null,
          indication: item.Indications || "",
        }));

      // 从 URL 或 API 获取疾病名称
      const diseaseName = query.name || "";

      // 解析 API 返回的症状和子疾病（逗号分隔的字符串）
      const symptomsStr = this.apiData.info.symptoms || "";
      const subDiseasesStr = this.apiData.info.subDiseases || "";
      const relatedSymptoms = symptomsStr
        ? symptomsStr.split("。").filter((s) => s.trim())
        : [];
      const subDiseases = subDiseasesStr
        ? subDiseasesStr.split("。").filter((s) => s.trim())
        : [];

      return {
        name: diseaseName,
        department_name: query.department_name || "",
        definition: this.apiData.info.description || `${diseaseName}`,
        related_symptoms: relatedSymptoms,
        sub_diseases: subDiseases,
        western_medicines: westernMedicines.length > 0 ? westernMedicines : [],
        chinese_medicines: chineseMedicines.length > 0 ? chineseMedicines : [],
      };
    },
  },
  mounted() {
    this.fetchMedicines();
  },
  methods: {
    async fetchMedicines() {
      const queryId = this.$route.query.id || 38;
      this.loading = true;
      try {
        const response = await axios.get(
          `http://192.168.10.13:8087/ug/disease-department/medicines?id=${queryId}`,
        );
        if (response.data && response.data.data) {
          this.apiData = response.data.data;
        }
      } catch (error) {
        console.error("获取药品列表失败:", error);
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.back();
    },
    goToDrugDetail(drugId) {
      this.$router.push({
        path: "/drug-detail",
        query: { id: drugId },
      });
    },
    onSearch(keyword) {
      this.searchKeyword = keyword;
    },
  },
};
</script>

<style scoped>
/* 自定义滚动条 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.04);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(37, 99, 235, 0.3);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(37, 99, 235, 0.5);
}

.medical-page {
  min-height: 100vh;
  background: radial-gradient(
      circle at top center,
      rgba(37, 99, 235, 0.08),
      transparent 26%
    ),
    linear-gradient(180deg, #f6fbff 0%, #eef7ff 45%, #f8fafc 100%);
  color: #0f172a;
}

.anchor-bar,
.disease-hero,
.page-layout {
  max-width: 1240px;
  margin: 0 auto;
  padding-left: 24px;
  padding-right: 24px;
}

.disease-hero {
  padding-top: 28px;
}

.disease-hero__main,
.content-card {
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
}

.disease-hero__main,
.content-card {
  padding: 28px;
}

.anchor-bar a {
  display: none;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  margin-top: 16px;
  padding: 0 10px;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 0;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
}

.back-btn:hover {
  color: #0f766e;
}

.back-btn__icon {
  font-size: 16px;
}

.breadcrumb-separator {
  color: #94a3b8;
  font-size: 14px;
  margin: 0 4px;
}

.breadcrumb-dept {
  font-size: 15px;
  color: #64748b;
  font-weight: 500;
}

.breadcrumb-name {
  font-size: 16px;
  color: #0f172a;
  font-weight: 700;
}

.page-breadcrumb {
  color: #64748b;
  font-size: 13px;
}

.hero-title-row {
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  gap: 18px;
}

.hero-title-row h1 {
  margin: 0;
  font-size: 40px;
}

.hero-definition {
  margin: 14px 0 0;
  color: #475569;
  line-height: 1.9;
  font-size: 16px;
  max-width: 840px;
}

.hero-meta {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-end;
}

.hero-meta__item {
  padding: 9px 14px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.08);
  color: #2563eb;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.hero-tags {
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.hero-tags span {
  padding: 10px 14px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
  font-size: 14px;
  font-weight: 600;
}

.anchor-bar {
  padding-top: 20px;
  padding-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.anchor-bar a {
  text-decoration: none;
  color: #0f172a;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  font-size: 14px;
  font-weight: 600;
}

.page-layout {
  padding-bottom: 36px;
}

.page-main {
  display: grid;
  gap: 20px;
}

.content-card--overview {
  background: #ffffff;
  margin-bottom: 20px;
  padding: 24px 28px;
}

.disease-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 16px;
}

.disease-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.disease-subtitle {
  font-size: 14px;
  color: #666;
}

.disease-definition {
  font-size: 14px;
  line-height: 1.8;
  color: #333;
  margin: 0 0 20px 0;
}

.disease-info-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.disease-info-item {
  display: flex;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.6;
}

.disease-info-label {
  color: #666;
  flex-shrink: 0;
}

.disease-info-content {
  color: #333;
  flex: 1;
}

/* 空状态样式 */
.empty-medicine {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.empty-medicine__icon {
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  opacity: 0.8;
}

.empty-medicine__icon svg {
  width: 100%;
  height: 100%;
}

.empty-medicine__title {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.empty-medicine__desc {
  font-size: 14px;
  color: #9ca3af;
  margin: 0;
}

.overview-hero {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.overview-hero__main {
  min-width: 0;
  flex: 1;
}

.overview-kicker {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.08);
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.overview-name {
  margin: 14px 0 0;
  font-size: 52px;
  line-height: 1.05;
  letter-spacing: -0.04em;
  color: #0f172a;
}

.overview-summary {
  margin: 14px 0 0;
  max-width: 860px;
  color: #334155;
  font-size: 15px;
  line-height: 1.85;
}

.overview-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(120px, 1fr));
  gap: 12px;
  width: 296px;
  flex-shrink: 0;
}

.overview-stat {
  padding: 16px 16px 14px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(37, 99, 235, 0.12);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.82);
}

.overview-stat__label {
  display: block;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.overview-stat strong {
  display: block;
  margin-top: 10px;
  color: #0f172a;
  font-size: 30px;
  line-height: 1;
}

.section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.section-head h2 {
  margin: 0;
  font-size: 22px;
}

.section-head span {
  white-space: nowrap;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.08);
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
}

.overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.4fr);
  gap: 16px;
}

.overview-block {
  padding: 16px 18px 18px;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95), #f8fbff);
  border: 1px solid rgba(37, 99, 235, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.88);
}

.overview-block--symptom {
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.95), #ffffff);
}

.overview-block--subdisease {
  background: linear-gradient(180deg, rgba(240, 253, 250, 0.95), #ffffff);
}

.overview-block__head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}

.overview-block__label {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.overview-block__count {
  white-space: nowrap;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.08);
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
}

.pill-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.pill-grid--compact {
  gap: 8px;
}

.pill-grid--tight {
  gap: 8px;
}

.pill-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.08);
  color: #0f172a;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
}

.pill-link--static {
  cursor: default;
}

.pill-link--symptom {
  background: rgba(37, 99, 235, 0.08);
  border-color: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
}

.pill-link--subdisease {
  background: rgba(15, 118, 110, 0.08);
  border-color: rgba(15, 118, 110, 0.14);
  color: #0f766e;
}

.medicine-grid,
.prescription-grid {
  display: grid;
  gap: 16px;
}

.medicine-grid--catalog {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.medicine-card,
.prescription-card {
  padding: 18px;
  border-radius: 22px;
  background: linear-gradient(180deg, #f8fbff, #ffffff);
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.medicine-card--western {
  position: relative;
  overflow: hidden;
  padding: 20px;
  background: radial-gradient(
      circle at top right,
      rgba(37, 99, 235, 0.09),
      transparent 34%
    ),
    linear-gradient(180deg, #f8fbff, #ffffff 76%);
  border-color: rgba(37, 99, 235, 0.14);
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.78);
}

.medicine-card--western::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #2563eb, transparent 72%);
}

.medicine-card--warm {
  background: linear-gradient(180deg, #fff9f2, #ffffff);
  border-color: rgba(249, 115, 22, 0.12);
}

.medicine-card--soft {
  background: linear-gradient(180deg, #f4fbf8, #ffffff);
  border-color: rgba(15, 118, 110, 0.12);
}

.medicine-card__label-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}

.medicine-card__label,
.medicine-card__review {
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.medicine-card__label {
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
}

.medicine-card__review {
  background: rgba(15, 118, 110, 0.1);
  color: #0f766e;
}

.medicine-card__top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.medicine-card h3,
.prescription-card h3 {
  margin: 0;
  font-size: 17px;
  line-height: 1.5;
}

.medicine-card--western h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.medicine-card__price {
  white-space: nowrap;
  color: #c2410c;
  font-size: 13px;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(249, 115, 22, 0.1);
  border: 1px solid rgba(249, 115, 22, 0.12);
}

.medicine-card__divider {
  width: 100%;
  height: 1px;
  margin: 14px 0 12px;
  background: linear-gradient(
    90deg,
    rgba(37, 99, 235, 0.16),
    rgba(15, 23, 42, 0.05)
  );
}

.medicine-card p,
.prescription-card p {
  margin: 12px 0 0;
  color: #475569;
  line-height: 1.85;
  font-size: 14px;
}

.medicine-card--western p {
  margin-top: 0;
}

@media (max-width: 1080px) {
  .page-header {
    padding: 0;
    flex-wrap: wrap;
  }

  .breadcrumb-dept {
    font-size: 13px;
  }

  .breadcrumb-name {
    font-size: 14px;
  }

  .back-btn {
    padding: 6px 12px;
    font-size: 13px;
  }

  .disease-hero,
  .anchor-bar,
  .page-layout {
    padding-left: 16px;
    padding-right: 16px;
  }

  .disease-hero__main,
  .content-card {
    padding: 20px;
  }

  .medicine-grid--catalog {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .overview-hero,
  .overview-grid,
  .hero-title-row,
  .medicine-card__top,
  .overview-block__head {
    flex-direction: column;
  }

  .overview-hero {
    align-items: stretch;
  }

  .overview-grid {
    display: grid;
    grid-template-columns: 1fr;
  }

  .overview-stats {
    width: 100%;
  }

  .hero-title-row h1 {
    font-size: 32px;
  }

  .overview-name {
    font-size: 38px;
  }

  .hero-meta {
    align-items: flex-start;
  }

  .pill-link {
    width: auto;
  }
}

@media (max-width: 1280px) {
  .medicine-grid--catalog {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .medicine-grid--catalog {
    grid-template-columns: 1fr;
  }
}
</style>
